import { useEffect, useMemo, useRef, useState } from 'react'
import { useFiscalYear } from '@/lib/fiscalYear'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
  computeBudgetTotals,
  lookupBudgetCodes,
  budgetHealth,
  type BudgetCode,
  type SearchableCode,
  type Transaction,
} from '@budgetmonkey/shared'
import { Badge, Input, Money } from '@/components/ui'
import { useBudgetCodes } from '@/features/budgetCodes/hooks'
import { useTransactions } from '@/features/transactions/hooks'

/**
 * Code lookup — REQUIREMENTS.md §7.5.
 *
 * A first-class workflow, not a drill-down: Matt is standing at a vendor
 * deciding whether to buy something and needs an answer in under three
 * seconds. Three consequences:
 *
 *   - It opens from anywhere, including a keyboard shortcut on desktop.
 *   - Results carry the remaining balance inline, so the common question
 *     ("do I have money left in 610?") is answered without a second tap.
 *   - Everything is computed from already-cached data. No query fires when
 *     this opens; if the app has been used at all this session, it's instant.
 *
 * External codes are hidden behind a toggle (§7.7) — they have no balance and
 * would only dilute the results Matt is actually after.
 */

function toSearchable(code: BudgetCode): SearchableCode {
  return {
    id: code.id ?? '',
    code: code.code,
    displayName: code.displayName,
    shortLabel: code.shortLabel,
    aliases: code.aliases,
    external: code.external,
    active: code.active,
  }
}

export function CodeLookup({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const navigate = useNavigate()
  const fiscalYear = useFiscalYear()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const [query, setQuery] = useState('')
  const [includeExternal, setIncludeExternal] = useState(false)
  const [highlighted, setHighlighted] = useState(0)

  const { data: codes = [] } = useBudgetCodes(fiscalYear)
  const { data: transactions = [] } = useTransactions(fiscalYear)

  const byId = useMemo(
    () => new Map(codes.map((c) => [c.id ?? '', c])),
    [codes],
  )
  const searchable = useMemo(() => codes.map(toSearchable), [codes])

  const transactionsByCode = useMemo(() => {
    const map = new Map<string, Transaction[]>()
    for (const t of transactions) {
      const list = map.get(t.budgetCodeId)
      if (list === undefined) map.set(t.budgetCodeId, [t])
      else list.push(t)
    }
    return map
  }, [transactions])

  const results = useMemo(
    () => lookupBudgetCodes(query, searchable, { includeExternal, limit: 25 }),
    [query, searchable, includeExternal],
  )

  useEffect(() => setHighlighted(0), [query, includeExternal])

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog === null) return
    if (open && !dialog.open) {
      dialog.showModal()
      // Focus the field directly so the mobile keyboard comes up with it.
      inputRef.current?.focus()
    } else if (!open && dialog.open) {
      dialog.close()
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      setQuery('')
      setIncludeExternal(false)
    }
  }, [open])

  function choose(codeId: string) {
    onClose()
    void navigate(`/codes/${codeId}`)
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlighted((i) => Math.min(i + 1, results.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlighted((i) => Math.max(i - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const target = results[highlighted]
      if (target !== undefined) choose(target.code.id)
    }
  }

  // `mx-auto` below is explicit because Tailwind's preflight resets the margin
  // that would otherwise centre a <dialog>, leaving it pinned to the left edge.
  // Same cause as the budget code picker, and easy to reintroduce on any new
  // dialog, since nothing about the markup looks wrong.
  return createPortal(
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose()
      }}
      aria-label="Look up a budget code"
      className="mx-auto mt-[8vh] w-[min(36rem,94vw)] rounded-xl p-0 shadow-xl backdrop:bg-slate-900/40"
    >
      <div className="border-b border-slate-200 p-4">
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a code, alias, or name — 580, travel, dues"
          aria-label="Look up a budget code"
          autoComplete="off"
          type="search"
          className="text-lg"
        />
      </div>

      <div className="max-h-[60vh] overflow-y-auto">
        {results.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-slate-500">
            {query.trim() === ''
              ? 'No budget codes yet.'
              : `Nothing matches "${query.trim()}".`}
          </p>
        ) : (
          <ul>
            {results.map((result, index) => {
              const code = byId.get(result.code.id)
              return (
                <li key={result.code.id}>
                  <button
                    type="button"
                    onClick={() => choose(result.code.id)}
                    onMouseEnter={() => setHighlighted(index)}
                    className={`flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left transition ${
                      index === highlighted ? 'bg-slate-100' : ''
                    }`}
                  >
                    <span className="font-medium text-slate-900">
                      {result.code.shortLabel}
                    </span>
                    {result.code.external && <Badge tone="warn">External</Badge>}
                    {result.detail !== '' && (
                      <span className="text-xs text-slate-400">{result.detail}</span>
                    )}

                    {code !== undefined && !code.external && (
                      <RemainingHint
                        code={code}
                        transactions={transactionsByCode.get(result.code.id) ?? []}
                      />
                    )}

                    <span className="w-full font-mono text-xs text-slate-500">
                      {result.code.code}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={includeExternal}
            onChange={(e) => setIncludeExternal(e.target.checked)}
            className="size-4 rounded border-slate-300"
          />
          Include external codes
        </label>
        <span className="text-xs text-slate-400">↑↓ to move · Enter to open</span>
      </div>
    </dialog>,
    document.body,
  )
}

/** Remaining balance inline — the answer to "can I buy this?" without a tap. */
function RemainingHint({
  code,
  transactions,
}: {
  code: BudgetCode
  transactions: readonly Transaction[]
}) {
  const totals = computeBudgetTotals(code.beginningBalance, transactions)
  const health = budgetHealth(totals, code.alertThresholdPercent)
  return (
    <span
      className={`ml-auto text-sm font-medium ${
        health === 'over'
          ? 'text-red-700'
          : health === 'approaching'
            ? 'text-amber-800'
            : 'text-slate-700'
      }`}
    >
      <Money value={totals.remaining} /> left
    </span>
  )
}
